import { FC, useMemo, useState, useRef } from "react";
import { ChartWrapper } from "../../../../../../components/ChartWrapper";
import { PeriodSelector } from "../../../../../../components/PeriodSelector";
import { useWorkspaceStore } from "../../../../../../store/workspace/WorkspaceStore";
import { determineChartContent, hasNoData } from "../../../../utils";
import { useGetCompetitorTrendData } from "./hooks/useGetCompetitorTrendData";
import { LineChartColors } from "../../../../../../components/charts/constants";
import { BaseDateDataChart } from "../../../../../../components/charts/BaseDateDataChart/BaseDateDataChart";
import { useCombinedFilters } from "../../../../../../hooks/useCombinedFilters";
import * as am4charts from "@amcharts/amcharts4/charts";
import { CHART_CONSTANTS } from "../../../constants";

const { chartTitle, baseId, tooltip, defaultSelectedPeriod } =
  CHART_CONSTANTS.competitorMarketPenetrationTrend;

export const CompetitorMarketPenetrationTrend: FC = () => {
  const [selectedPeriod, setSelectedPeriod] = useState(defaultSelectedPeriod);
  const chartRef = useRef<am4charts.XYChart>(null);
  const combinedFilters = useCombinedFilters();
  const competitorsProductIds = useWorkspaceStore(
    (state) => state.draft?.configuration.competitorsProductIds || []
  );
  const localizeInstalls = useWorkspaceStore(
    (state) => state.draft?.configuration?.localizeInstalls ?? true
  );

  const { loading, data } = useGetCompetitorTrendData({
    companyFilters: combinedFilters ?? {},
    productIds: competitorsProductIds,
    localizeInstalls,
    lookbackYears: Number(selectedPeriod),
  });

  const csvFieldMappings = useMemo(() => {
    const mappings: Record<string, string> = {
      date: "Date",
    };

    if (!data || loading || data.length === 0 || !data[0].series) {
      return mappings;
    }

    const [firstDataPoint] = data;
    firstDataPoint.series.forEach((seriesItem) => {
      const baseKey = seriesItem.name.toLowerCase().replace(/[\s&]+/g, "_");
      mappings[`series_${baseKey}`] = `${seriesItem.name} (%)`;
      mappings[`series_${baseKey}_count`] =
        `${seriesItem.name} (# of companies)`;
    });

    return mappings;
  }, [data, loading]);

  const noDataReturned = useMemo(
    () => hasNoData(loading, data),
    [data, loading]
  );

  const chartContent = useMemo(() => {
    return determineChartContent({
      productIds: competitorsProductIds,
      isLoading: loading,
      noDataReturned,
      baseId,
      chartToRender: (
        <BaseDateDataChart
          id={`${baseId}-chart`}
          chartData={data}
          inputDateFormat="yyyy-MM-dd"
          baseColor={LineChartColors}
          yAxisLabel="Market Penetration (%)"
          showCustomTooltip={true}
          showPercentageSymbol
          ref={chartRef}
        />
      ),
    });
  }, [data, loading, noDataReturned, competitorsProductIds]);

  return (
    <ChartWrapper
      id={`${baseId}-chart-wrapper`}
      title={chartTitle}
      tooltipTitle={tooltip}
      chart={chartContent}
      chartRef={chartRef}
      noData={noDataReturned || competitorsProductIds.length === 0}
      isLoading={loading}
      titleExtras={
        <PeriodSelector
          id={`${baseId}-period-select`}
          selectedPeriod={selectedPeriod}
          onChange={setSelectedPeriod}
        />
      }
      csvFieldMappings={csvFieldMappings}
    />
  );
};
